"use client"

import { ComponentConfig } from "@measured/puck"
import { DatePicker, Form, Input, Select, Spin } from "antd"
import TextArea from "antd/es/input/TextArea"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { getSettings, iconSubmit } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { DataSettingItem } from "../../../types"
import "./index.css"

// Interface for form props
export interface FormCustomizeTour extends CommonStylesProps {}

export const RenderConfig: ComponentConfig<FormCustomizeTour> = {
  render: ({ styles, responsiveType, className }) => {
    const id = `form-register-${Math.random().toString(36).substr(2, 9)}`
    const [form] = Form.useForm()
    const [isPopupVisible, setIsPopupVisible] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")

    const [formData, setFormData] = useState({
      emailTo: "",
      name: "",
      email: "",
      phone: "",
      groupSize: "",
      start: null,
      end: null,
      location: "",
      adventureType: "",
      accommodation: "",
      difficulty: "",
      distance: "",
      additionalInfo: "",
    })

    const fetchSetting = async () => {
      try {
        const response = await getSettings()
        const emailTo = response.find((item: DataSettingItem) => item.attributes.key === "customizetourEmail")
          ?.attributes.value
        setFormData(prev => ({ ...prev, emailTo: emailTo }))
      } catch (error) {
        console.error("Error fetching setting data:", error)
      }
    }

    useEffect(() => {
      fetchSetting()
    }, [])

    const closePopup = () => {
      setMessage("Đăng ký thành công!")
      setIsPopupVisible(false)
      form.resetFields()
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      try {
        setIsLoading(true)
        await form.validateFields()
        const response = await fetch("/api/email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: "[Thông báo] Đăng ký tour",
            emailTo: formData.emailTo,
            content: `
              Họ và tên: ${form.getFieldValue("name")}
              Email: ${form.getFieldValue("email") || "Không xác định"}
              Số điện thoại: ${form.getFieldValue("phone")}
              Số lượng người: ${form.getFieldValue("groupSize")}
              Ngày bắt đầu: ${form.getFieldValue("start")?.format("DD/MM/YYYY") || "Không xác định"}
              Ngày kết thúc: ${form.getFieldValue("end")?.format("DD/MM/YYYY") || "Không xác định"}
              Địa điểm: ${form.getFieldValue("location")}
              Loại hình phiêu lưu: ${form.getFieldValue("adventureType")}
              Loại hình lưu trú: ${form.getFieldValue("accommodation")}
              Mức độ thử thách: ${form.getFieldValue("difficulty")}
              Khoảng cách: ${form.getFieldValue("distance")}
              Yêu cầu thêm: ${form.getFieldValue("additionalInfo")}
            `,
          }),
        })
        if (!response.ok) {
          throw new Error("Failed to submit data")
        }
        setIsPopupVisible(true)
        setFormData({
          emailTo: "",
          name: "",
          email: "",
          phone: "",
          groupSize: "",
          start: null,
          end: null,
          location: "",
          adventureType: "",
          accommodation: "",
          difficulty: "",
          distance: "",
          additionalInfo: "",
        })
        form.resetFields()
      } catch (error) {
        console.error("Error:", error)
        setMessage("Đã xảy ra lỗi khi đăng ký!")
      } finally {
        setIsLoading(false)
      }
    }

    const mergedStyles = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }

    const responsiveCSS = generateResponsiveCSS(id, mergedStyles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div className={`${id} ${twMerge(className)} w-full text-center form-cutomize-tour`}>
          <div className="w-full text-center">
            <Form
              form={form}
              layout="vertical"
              labelAlign="right"
              onFinish={handleSubmit}
              className="mx-auto sm:space-y-3 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 sm:gap-3 gap-4 text-base leading-6 font-medium">
                <Form.Item
                  name="name"
                  label="Your name"
                  rules={[{ required: true, message: "Please enter your name" }]}
                >
                  <Input
                    placeholder="Your name"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                  />
                </Form.Item>

                <Form.Item name="email" label="Email">
                  <Input placeholder="Email" className="h-10 !rounded-lg" />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[{ required: true, message: "Please enter your phone number" }]}
                >
                  <Input
                    placeholder="Phone"
                    className="h-10 !rounded-lg placeholder:text-base
                    placeholder:leading-6
                    placeholder:font-medium"
                  />
                </Form.Item>

                <Form.Item
                  name="start"
                  label="Start"
                  rules={[{ required: true, message: "Please select a start date" }]}
                >
                  <DatePicker placeholder="mm/dd/yy" className="w-full h-10 !rounded-lg" format="MM/DD/YYYY" />
                </Form.Item>

                <Form.Item name="end" label="End" rules={[{ required: true, message: "Please select an end date" }]}>
                  <DatePicker
                    className="w-full h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                    format="DD/MM/YYYY"
                    placeholder="mm/dd/yy"
                  />
                </Form.Item>
                <Form.Item
                  name="groupSize"
                  label="How many riders in your group?"
                  rules={[{ required: true, message: "Please enter group size" }]}
                >
                  <Input
                    placeholder="How many riders?"
                    className="h-10 sm:pt-0 pt-2 !rounded-lg placeholder:text-base
                    placeholder:leading-6
                    placeholder:font-medium"
                  />
                </Form.Item>
              </div>

              <div>
                <Form.Item
                  name="location"
                  label="Where would you like to visit?"
                  rules={[{ required: true, message: "Please specify a location" }]}
                >
                  <TextArea
                    placeholder="Message"
                    className="!rounded-lg !h-[92px] placeholder:text-base placeholder:leading-6 placeholder:font-medium font-Josefin_Sans"
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-3 gap-4 text-base leading-6 font-medium">
                <Form.Item
                  name="adventureType"
                  label="What sort of adventure would suit you?"
                  rules={[{ required: true, message: "Please select an adventure type" }]}
                >
                  <Select
                    placeholder="Excursion"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                  >
                    <Select.Option value="excursion">Excursion</Select.Option>
                    <Select.Option value="trekking">Trekking</Select.Option>
                    <Select.Option value="cycling">Cycling</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="accommodation"
                  label="What style of accommodation would suit you?"
                  rules={[{ required: true, message: "Please select accommodation type" }]}
                >
                  <Select
                    placeholder="5-star"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                  >
                    <Select.Option value="5-star">5-star</Select.Option>
                    <Select.Option value="4-star">4-star</Select.Option>
                    <Select.Option value="3-star">3-star</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="difficulty"
                  label="How challenging would you like the tour?"
                  rules={[{ required: true, message: "Please select difficulty level" }]}
                >
                  <Select
                    placeholder="Easy"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                  >
                    <Select.Option value="easy">Easy</Select.Option>
                    <Select.Option value="moderate">Moderate</Select.Option>
                    <Select.Option value="hard">Hard</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="distance"
                  label="What sort of distances would you like to ride daily?"
                  rules={[{ required: true, message: "Please select a distance range" }]}
                >
                  <Select
                    placeholder="<10 km"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-6 placeholder:font-medium"
                  >
                    <Select.Option value="<10">&lt;10 km</Select.Option>
                    <Select.Option value="10-20">10-20 km</Select.Option>
                    <Select.Option value="20-50">20-50 km</Select.Option>
                    <Select.Option value=">50">&gt;50 km</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <div>
                <Form.Item name="additionalInfo" label="Let us know your requirements">
                  <TextArea
                    placeholder="Message"
                    className="!rounded-lg !h-[92px] placeholder:text-base placeholder:leading-6 placeholder:font-medium font-Josefin_Sans"
                  />
                </Form.Item>
              </div>

              <button
                type="submit"
                onClick={handleSubmit}
                className="flex justify-center items-center gap-2 w-full !mt-6 bg-[#0B9444] font-semibold text-base leading-[22px] text-white py-[13px] rounded-xl hover:bg-[#007B73]"
              >
                <img src={iconSubmit} className="h-[14px] w-[14px]" />
                Submit
              </button>
            </Form>
          </div>

          {isLoading && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <Spin />
            </div>
          )}

          {isPopupVisible && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white p-8 rounded shadow-md relative">
                <button
                  onClick={closePopup}
                  className="absolute top-0 text-[30px] right-2 text-gray-600 hover:text-gray-800"
                  aria-label="Close"
                >
                  &times;
                </button>
                <p className="text-lg font-semibold">{message}</p>
              </div>
            </div>
          )}
        </div>
      </>
    )
  },
}
